import React from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PageSection from "../PageSection";
import { lightTheme } from "../../styles/theme";
import type { NoticeWithType } from ".";

interface DraftsListProps {
  drafts: NoticeWithType[];
  onDraftClick: (notice: NoticeWithType) => void;
  onDeleteDraft: (notice: NoticeWithType, e: React.MouseEvent) => void;
}

const formatLastEdited = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const noticeDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const timeStr = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (noticeDate.getTime() === today.getTime()) {
      return `Today at ${timeStr}`;
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (noticeDate.getTime() === yesterday.getTime()) {
      return `Yesterday at ${timeStr}`;
    }

    return (
      date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year:
          date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      }) + ` at ${timeStr}`
    );
  } catch {
    return "Unknown";
  }
};

const DraftsList: React.FC<DraftsListProps> = ({
  drafts,
  onDraftClick,
  onDeleteDraft,
}) => {
  const theme = useTheme();

  if (drafts.length === 0) return null;

  return (
    <PageSection>
      <Box
        sx={{
          mb: 3,
          p: 3,
          border: "1px solid",
          borderColor: lightTheme.colors.surfaceTealMist,
          borderRadius: 2,
          bgcolor: lightTheme.colors.surfaceTealMist,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            Pick up where you left off
          </Typography>
          <Chip
            label={drafts.length}
            size="small"
            sx={{
              bgcolor: theme.palette.success.light,
              color: lightTheme.colors.white,
              fontWeight: 600,
              height: 24,
              fontSize: "0.75rem",
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: "1rem",
            color: "text.secondary",
            mb: 2,
          }}
        >
          Your drafts are safely saved and ready whenever you are.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {drafts.map((notice) => (
            <Paper
              key={notice.noticeId}
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: theme.palette.common.white,
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
                },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1.5,
                  bgcolor: theme.palette.grey[100],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                <img
                  src="/icons/edit.webp"
                  alt="Edit"
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: "text.primary",
                    mb: 0.5,
                  }}
                >
                  {notice.title || "Untitled Draft"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: (theme) => theme.typography.body2.fontSize,
                    color: "text.secondary",
                  }}
                >
                  Last edited {formatLastEdited(notice.createdAt)}
                </Typography>
              </Box>

              {/* Actions */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  component="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDraftClick(notice);
                  }}
                  sx={{
                    fontSize: (theme) => theme.typography.body2.fontSize,
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Continue editing
                  <ArrowForwardIcon sx={{ fontSize: (theme) => theme.typography.body1.fontSize }} />
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Remove draft"
                  onClick={(e) => onDeleteDraft(notice, e)}
                  sx={{
                    color: theme.palette.error.main,
                    "&:hover": {
                      bgcolor: lightTheme.colors.errorOverlay10,
                    },
                  }}
                  data-testid="view-notices-delete-draft-button"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    </PageSection>
  );
};

export default DraftsList;
